import React, { useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('pt-BR', ptBR);

// Convert a Date to local "YYYY-MM-DD" (date-only sites — preserves the
// existing onChange contract: consumers receive the same ISO date string
// the native <input type="date"> used to emit, and store it directly via
// Patch into Mongo's `date` / `start_date` fields).
const toIsoDate = (d) => {
  if (!(d instanceof Date) || isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Convert a Date to a "YYYY-MM-DDTHH:mm" datetime-local-style string —
// used for the new datetime sites migrated under F.6.
const toIsoDateTime = (d) => {
  if (!(d instanceof Date) || isNaN(d.getTime())) return '';
  const isoDate = toIsoDate(d);
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${isoDate}T${hh}:${mi}`;
};

// Parse the prop value (accepts Date, ISO string, or undefined/null/false/"").
const parseValue = (raw) => {
  if (raw instanceof Date) return raw;
  if (typeof raw === 'string' && raw) {
    // Treat a YYYY-MM-DD string as local-noon to avoid timezone roll-back.
    const isoOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);
    const d = new Date(isoOnly ? raw + 'T12:00:00' : raw);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
};

const DateInput = (props) => {
  const { i18n } = useTranslation();
  const {
    // Existing 4-consumer contract: `date` is the ISO string in.
    date,
    // New consumers (post-migration): `value` is also accepted as an alias.
    value,
    onChange,
    showTime = false,
    defaultToToday,
    name,
    required,
    className,
    min,
    max,
    placeholder,
    style,
    ...rest
  } = props;

  // Default-to-today preserves the legacy DateInput behavior (used by the
  // bare-form mode in AddBirth/AddDeath where `date` is undefined).
  const incoming = date !== undefined ? date : value;
  const shouldDefaultToday = defaultToToday !== undefined
    ? defaultToToday
    : (incoming === undefined || incoming === false || incoming === null || incoming === '');

  // Uncontrolled use (a plain form read with FormData, e.g. AddFinance
  // through DataCollectionAdd) passes neither `date` nor `value`: then the
  // picked date has to be remembered here, or the picker snaps back to today
  // on every render. Controlled use keeps following its prop as before.
  const controlled = date !== undefined || value !== undefined;
  const [picked, setPicked] = useState(() => parseValue(incoming) || (shouldDefaultToday ? new Date() : null));
  const parsed = parseValue(incoming);
  const selected = controlled ? (parsed || (shouldDefaultToday ? new Date() : null)) : picked;

  const locale = i18n.language === 'pt-BR' ? 'pt-BR' : undefined;
  const dateFormat = showTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy';

  const handleChange = (d) => {
    if (!controlled) setPicked(d instanceof Date && !isNaN(d.getTime()) ? d : null);
    if (!onChange) return;
    if (!(d instanceof Date) || isNaN(d.getTime())) {
      onChange('');
      return;
    }
    onChange(showTime ? toIsoDateTime(d) : toIsoDate(d));
  };

  const inputName = (name != null && name !== '') ? name : 'date';
  const isRequired = required !== undefined ? required : true;
  const minDate = parseValue(min);
  const maxDate = parseValue(max);

  // The form value is the ISO date, in a hidden input under `name`. The
  // picker's own text box shows dd/MM/yyyy and has no name, so FormData
  // never picks up the display text — it did, and forms read with FormData
  // saved dates like "24/09/2026" (AddBirth had worked around it by hand).
  const isoValue = selected ? (showTime ? toIsoDateTime(selected) : toIsoDate(selected)) : '';

  return (
    <>
    <DatePicker
      selected={selected}
      onChange={handleChange}
      locale={locale}
      dateFormat={dateFormat}
      showTimeSelect={showTime}
      timeFormat="HH:mm"
      timeIntervals={15}
      required={isRequired}
      className={`date-input ${className || ''}`.trim()}
      placeholderText={placeholder}
      minDate={minDate || undefined}
      maxDate={maxDate || undefined}
      wrapperClassName="date-input-wrapper"
      autoComplete="off"
      {...rest}
    />
    <input type="hidden" name={inputName} value={isoValue} />
    </>
  );
};

export default DateInput;
